import { ForwardedRef, MouseEvent, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import { STATUS_MAP } from "components/shared/UserBookStatuses";
import StatusColors, {
  sharedButtonClasses,
} from "features/BookButton/constants/StatusColors";
import Icon from "hardcover-ui/components/Icon";
import LoadingSpinner from "hardcover-ui/components/LoadingSpinner";
import { buttonTheme } from "hardcover-ui/theme/components/button";
import useCurrentUser from "hooks/fragments/useCurrentUser";
import useMount from "hooks/useMount";
import useCurrentUserId from "hooks/user/useCurrentUserId";
import faChevronDown from "icons/solid/chevron-down.svg";
import { useApolloClient } from "lib/apollo/client";
import classNames from "lib/classNames";
import { insertUserBook } from "services/UserBookService/UserBookServiceClient";
import { getActiveBookId } from "store/bookButton/bookButtonSelector";
import { bookButtonActions } from "store/bookButton/bookButtonSlice";
import { setUserBook } from "store/books/booksSlice";
import { uiActions } from "store/ui/uiSlice";
import { slugIdConstants } from "types/UserBookStatusType";

import { BookButtonSizeType } from "../BookButton";

export default function StatusButton({
  el,
  bookId,
  referrerUserId,
  statusId,
  size,
  onClick,
}: {
  bookId: number;
  el: ForwardedRef<HTMLDivElement>;
  referrerUserId?: number;
  size: BookButtonSizeType;
  statusId: number | null | undefined;
  onClick?: any;
}) {
  const client = useApolloClient();
  const currentUserId = useCurrentUserId();
  const loggedIn = currentUserId !== null;
  const mounted = useMount();

  const dispatch = useDispatch();
  const defaultPrivacySettingId = useCurrentUser(
    u => u?.accountPrivacySettingId || 1,
  );
  const [open, setOpen] = useState(false);
  const activeBookId = useSelector(getActiveBookId);
  const uid = `book-button-${bookId}`;

  const { status, buttonClasses, accentColor, fillColor, noStatus, loading } =
    useMemo(() => {
      const statusColor = StatusColors[statusId || 0];
      return {
        status: STATUS_MAP[statusId] || STATUS_MAP[0],
        buttonClasses: statusColor.buttonClasses,
        accentColor: statusColor.accentColor,
        fillColor: statusColor.fillColor,
        noStatus: !statusId,
        loading: statusId === undefined,
      };
    }, [statusId]);

  // Show loading spinner until mounted to prevent hydration mismatch
  const isLoading = !mounted || loading;

  // Only open if drawer book matches this one
  useEffect(() => {
    if (open) {
      setOpen(activeBookId && bookId === activeBookId);
    }
  }, [activeBookId, bookId]);

  const markAsWantToRead = function (e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();

    if (!loggedIn) {
      // Not logged in, show login
      dispatch(uiActions.toggleLoginVisible("Login"));
      return;
    }

    // This will update cache and rerender this component
    insertUserBook({
      client,
      userBook: {
        userId: currentUserId,
        bookId: bookId,
        statusId: slugIdConstants["want-to-read"],
        privacySettingId: defaultPrivacySettingId,
        referrerUserId,
      },
    })
      .then(newUserBook => {
        if (newUserBook) {
          dispatch(setUserBook({ userBook: newUserBook }));
          dispatch(bookButtonActions.setUserBook({ userBook: newUserBook }));
          toast.success("Saved as Want to Read.");
        }
      })
      .catch(() => {
        toast.error("Error saving book. Try again?");
      });
  };

  return (
    <div
      ref={el}
      className={
        noStatus && size === "md"
          ? `${sharedButtonClasses} dark:border-gray-700 dark:bg-gray-700 font-medium overflow-hidden divide-x divide-gray-200 dark:divide-gray-600`
          : ""
      }
    >
      <button
        type="button"
        className={classNames(
          noStatus && size === "md"
            ? `space-x-2 py-2.5 px-3.5 ${buttonClasses} shadow-button active:shadow-none whitespace-nowrap`
            : classNames(
                `book-button ${uid}`,
                "space-x-2",
                sharedButtonClasses,
                buttonClasses,
                buttonTheme.size.lg,
              ),
        )}
        data-open={open ? "true" : "false"}
        aria-label="Change status"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (noStatus && size === "md") {
            markAsWantToRead(e);
          } else if (onClick) {
            onClick(e);
          }
        }}
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {isLoading ? (
          <LoadingSpinner className={`${accentColor} inline`} />
        ) : (
          <Icon icon={status.duoIcon} className={accentColor} size="lg" />
        )}

        <span
          className={classNames(
            accentColor,
            "font-semibold text-base whitespace-nowrap",
          )}
        >
          {size === "md" ? (
            <>{status.name}</>
          ) : (
            <span className="invisible">.</span>
          )}
        </span>
        {!(noStatus && size === "md") ? (
          <Icon icon={faChevronDown} className={fillColor} />
        ) : (
          false
        )}
      </button>

      {/* Drop down button only for no-status */}
      {noStatus && size === "md" ? (
        <button
          type="button"
          className={`book-button ${buttonClasses} py-2.5 px-3.5 shadow-button active:shadow-none`}
          data-open={open ? "true" : "false"}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            if (onClick) {
              onClick(e);
            }
          }}
          aria-label="Expand status menu"
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Icon icon={faChevronDown} className={fillColor} />
        </button>
      ) : (
        false
      )}
    </div>
  );
}
